<template>
  <div class="fuel-history-card" v-if="xeId">
    <div class="fh-header">
      <div class="fh-title">
        <i class="fa fa-gas-pump mr-1"></i>
        Lịch sử đổ xăng
      </div>

      <div class="fh-month-filter">
        <label class="fh-label">Tháng</label>
        <div class="fh-month-input-wrap">
          <button type="button" class="fh-month-nav" @click="changeMonth(-1)">
            <i class="fa fa-chevron-left"></i>
          </button>
          <input
            type="month"
            class="fh-month-input"
            v-model="monthInput"
            @change="onMonthChange"
          />
          <button type="button" class="fh-month-nav" @click="changeMonth(1)">
            <i class="fa fa-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Stats nhỏ -->
    <div class="fh-stats" v-if="items.length">
      <div class="fh-stat-item">
        <div class="label">Số lần đổ</div>
        <div class="value">{{ items.length }}</div>
      </div>
      <div class="fh-stat-item">
        <div class="label">Tổng lít</div>
        <div class="value">
          {{ totalLit.toLocaleString('vi-VN', { maximumFractionDigits: 1 }) }} L
        </div>
      </div>
      <div class="fh-stat-item">
        <div class="label">Tổng tiền</div>
        <div class="value text-danger">
          {{ totalTien.toLocaleString('vi-VN') }} đ
        </div>
      </div>
    </div>

    <!-- body -->
    <div class="fh-body">
      <!-- Loading -->
      <div v-if="loading" class="fh-state text-muted">
        <span class="spinner-border spinner-border-sm mr-1"></span>
        Đang tải lịch sử xăng dầu...
      </div>

      <!-- Error -->
      <div v-else-if="error" class="fh-state text-danger small">
        <i class="fa fa-exclamation-circle mr-1"></i>{{ error }}
      </div>

      <!-- Empty -->
      <div v-else-if="!items.length" class="fh-state small text-muted">
        Không có log xăng nào trong tháng này.
      </div>

      <!-- List -->
      <ul v-else class="fh-list">
        <li v-for="log in items" :key="log.id" class="fh-item">
          <div class="fh-row-top">
            <div class="fh-date">
              {{ formatDateTimeShort(log.ngayDo) }}
            </div>
            <div class="fh-money">
              {{ formatCurrency(log.soTien) }}
            </div>
          </div>

          <div class="fh-row-middle">
            <div class="fh-lit">
              {{ log.soLit }} L • {{ formatCurrency(log.donGia) }}/L
            </div>
            <div class="fh-odo" v-if="log.odoKmDauKy != null && log.odoKmCuoiKy != null">
              ODO: {{ log.odoKmDauKy }} → {{ log.odoKmCuoiKy }}
            </div>
          </div>

          <div class="fh-row-bottom" v-if="log.ghiChu">
            <i class="fa fa-sticky-note mr-1 text-muted"></i>
            <span class="small text-muted">{{ log.ghiChu }}</span>
          </div>
        </li>
      </ul>
    </div>
  </div>

  <div v-else class="fuel-history-card fuel-history-card--empty">
    <div class="fh-state small text-muted">
      Chưa xác định được xe hiện tại.
    </div>
  </div>
</template>

<script>
import { XangDauApi } from "@/services/xangDauService";

function pad2(n) {
  return String(n).padStart(2, "0");
}

export default {
  name: "TaiXeLichSuXangDau",
  props: {
    xeId: { type: Number, required: true },
    taiXeId: { type: Number, required: false }, // nếu muốn lọc theo tài xế
  },
  data() {
    return {
      loading: false,
      error: null,
      items: [],
      monthInput: null, // 'YYYY-MM'
      filters: {
        tuNgay: null,
        denNgay: null,
      },
    };
  },
  computed: {
    totalLit() {
      return this.items.reduce(
        (sum, l) => sum + Number(l.soLit || 0),
        0
      );
    },
    totalTien() {
      return this.items.reduce(
        (sum, l) => sum + Number(l.soTien || 0),
        0
      );
    },
  },
  methods: {
    toYmd(d) {
      const yyyy = d.getFullYear();
      const mm = pad2(d.getMonth() + 1);
      const dd = pad2(d.getDate());
      return `${yyyy}-${mm}-${dd}`;
    },

    initCurrentMonth() {
      const now = new Date();
      const y = now.getFullYear();
      const m = now.getMonth(); // 0-11

      const first = new Date(y, m, 1);
      const last = new Date(y, m + 1, 0);

      this.monthInput = `${y}-${pad2(m + 1)}`;
      this.filters.tuNgay = this.toYmd(first);
      this.filters.denNgay = this.toYmd(last);
    },

    onMonthChange() {
      if (!this.monthInput) {
        this.filters.tuNgay = null;
        this.filters.denNgay = null;
        this.loadData();
        return;
      }

      const parts = this.monthInput.split("-");
      if (parts.length !== 2) return;
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10);
      if (!year || !month) return;

      const first = new Date(year, month - 1, 1);
      const last = new Date(year, month, 0);
      this.filters.tuNgay = this.toYmd(first);
      this.filters.denNgay = this.toYmd(last);

      this.loadData();
    },

    changeMonth(offset) {
      if (!this.monthInput) {
        this.initCurrentMonth();
        this.loadData();
        return;
      }
      const [yStr, mStr] = this.monthInput.split("-");
      let y = parseInt(yStr, 10);
      let m = parseInt(mStr, 10);

      if (!y || !m) return;

      m += offset;
      if (m < 1) {
        m = 12;
        y -= 1;
      } else if (m > 12) {
        m = 1;
        y += 1;
      }

      this.monthInput = `${y}-${pad2(m)}`;
      this.onMonthChange();
    },

    formatCurrency(v) {
      if (v == null || isNaN(Number(v))) return "-";
      return Number(v).toLocaleString("vi-VN") + " đ";
    },

    formatDateTimeShort(iso) {
      if (!iso) return "";
      const d = new Date(iso);
      if (isNaN(d.getTime())) return "";
      const dd = pad2(d.getDate());
      const mm = pad2(d.getMonth() + 1);
      const hh = pad2(d.getHours());
      const mi = pad2(d.getMinutes());
      return `${dd}/${mm} ${hh}:${mi}`;
    },

    async loadData() {
      if (!this.xeId) return;

      this.loading = true;
      this.error = null;

      try {
        const params = {
          xeId: this.xeId,
          trangThai: "DA_DUYET", // tài xế chỉ xem log đã duyệt
          tuNgay: this.filters.tuNgay || undefined,
          denNgay: this.filters.denNgay || undefined,
          page: 0,
          size: 100,
          sort: "ngayDo,desc",
        };

        // nếu BE có support lọc theo tài xế
        if (this.taiXeId) {
          params.taiXeId = this.taiXeId;
        }

        const res = await XangDauApi.listLogs(params);
        const content = Array.isArray(res) ? res : res.content || [];
        this.items = content;
      } catch (e) {
        console.error("[TaiXeLichSuXangDau] loadData error", e);
        this.error =
          e?.response?.data?.message ||
          e?.message ||
          "Không tải được lịch sử xăng dầu.";
        this.items = [];
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.initCurrentMonth();
    this.loadData();
  },
};
</script>

<style scoped>
.fuel-history-card {
  background: #fff;
  border-radius: 16px;
  padding: 12px 14px;
  margin-top: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  font-size: 13px;
}

.fuel-history-card--empty {
  background: transparent;
  box-shadow: none;
  padding: 8px 0 0;
}

.fh-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  gap: 8px;
}

.fh-title {
  font-weight: 600;
  color: #2d3748;
  font-size: 14px;
  display: flex;
  align-items: center;
}

.fh-month-filter {
  text-align: right;
}

.fh-label {
  font-size: 11px;
  color: #718096;
  display: block;
}

.fh-month-input-wrap {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 2px;
}

.fh-month-input {
  border-radius: 999px;
  border: 1px solid #e2e8f0;
  padding: 4px 8px;
  font-size: 12px;
}

.fh-month-nav {
  border-radius: 999px;
  border: none;
  width: 24px;
  height: 24px;
  font-size: 11px;
  background: #edf2f7;
  color: #4a5568;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* stats */
.fh-stats {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
}

.fh-stat-item {
  flex: 1;
  background: #f7fafc;
  border-radius: 10px;
  padding: 6px 8px;
}

.fh-stat-item .label {
  font-size: 10px;
  color: #718096;
}

.fh-stat-item .value {
  font-size: 13px;
  font-weight: 600;
  color: #2d3748;
}

/* body */
.fh-body {
  border-top: 1px dashed #e2e8f0;
  margin-top: 6px;
  padding-top: 6px;
}

.fh-state {
  padding: 4px 0;
}

.fh-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.fh-item {
  padding: 6px 0;
  border-bottom: 1px solid #edf2f7;
}

.fh-item:last-child {
  border-bottom: none;
}

.fh-row-top {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
}

.fh-date {
  color: #4a5568;
}

.fh-money {
  font-weight: 600;
  color: #e53e3e;
}

.fh-row-middle {
  margin-top: 2px;
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #718096;
  gap: 6px;
  flex-wrap: wrap;
}

.fh-row-bottom {
  margin-top: 2px;
  display: flex;
  align-items: center;
}
</style>
